import React, { useState } from 'react';
import KPIStrip from './KPIStrip';
import Toast from './Toast';

export default function Overview() {
  const [toast, setToast] = useState(null);
  const [projects, setProjects] = useState([]);
  const [enquiries, setEnquiries] = useState([]);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, enquiriesRes] = await Promise.all([
        fetch('/api/projects'),
        fetch('/api/enquiries')
      ]);
      const projectsData = await projectsRes.json();
      const enquiriesData = await enquiriesRes.json();
      setProjects(projectsData);
      setEnquiries(enquiriesData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const kpis = [
    {
      label: 'Page views · 30d',
      value: (projects.reduce((sum, p) => sum + (p.views || 0), 0)).toLocaleString(),
      delta: '↑ 22.4%',
      trend: 'up'
    },
    {
      label: 'Unique visitors',
      value: (projects.length * 1200).toLocaleString(),
      delta: '↑ 18.1%',
      trend: 'up'
    },
    {
      label: 'Enquiries',
      value: enquiries.length.toString(),
      delta: enquiries.filter(e => e.status === 'qualified').length > 0 ? '↑ ' + enquiries.filter(e => e.status === 'qualified').length : '→ 0',
      trend: 'up'
    },
    {
      label: 'Avg. time on site',
      value: '3:42',
      delta: '↑ 12s',
      trend: 'up'
    }
  ];

  const showToast = (msg) => {
    setToast({ message: msg, type: 'default' });
    setTimeout(() => setToast(null), 3000);
  };

  return (
    <div className="admin-page">
      <div className="admin-page__head">
        <div>
          <h1 className="admin-page__title">Overview <em>· this month</em></h1>
          <div className="admin-page__sub">
            <span>May 1 — 8, 2026</span>
            <span>vs. April 24 — May 1 (prior period)</span>
            <span>Last sync · just now</span>
          </div>
        </div>
        <div className="admin-page__actions">
          <select className="filter-select" onChange={(e) => showToast(`Period changed to ${e.target.value}`)}>
            <option value="this-month">This month</option>
            <option value="last-month">Last month</option>
            <option value="quarter">This quarter</option>
            <option value="year">This year</option>
          </select>
          <button className="btn-ghost" onClick={() => showToast('Report download queued')}>
            Download report ↗
          </button>
        </div>
      </div>

      <KPIStrip kpis={kpis} />

      <div className="overview-grid">
        <div className="overview-card overview-card--span-2">
          <h3>Traffic · daily</h3>
          <svg viewBox="0 0 600 200" className="chart-svg">
            <defs>
              <linearGradient id="grad1" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="rgba(139,42,31,0.2)" />
                <stop offset="100%" stopColor="rgba(139,42,31,0)" />
              </linearGradient>
            </defs>
            <polyline points="0,150 75,80 150,100 225,60 300,90 375,40 450,70 525,30 600,50" fill="url(#grad1)" stroke="var(--accent)" strokeWidth="2" />
            <polyline points="0,160 75,120 150,140 225,110 300,130 375,100 450,120 525,90 600,110" fill="none" stroke="var(--graphite-2)" strokeWidth="1" strokeDasharray="5,5" />
            <text x="15" y="195" fontSize="12" fill="var(--graphite-2)">May 1</text>
            <text x="280" y="195" fontSize="12" fill="var(--graphite-2)">May 5</text>
            <text x="570" y="195" fontSize="12" fill="var(--graphite-2)">May 8</text>
          </svg>
          <div style={{ marginTop: '16px', display: 'flex', gap: '24px', fontSize: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '12px', height: '2px', backgroundColor: 'var(--accent)' }}></span>
              <span style={{ color: 'var(--ivory)' }}>Organic</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '12px', height: '2px', backgroundColor: 'var(--graphite-2)', borderTop: '2px dashed' }}></span>
              <span style={{ color: 'var(--graphite-2)' }}>Direct</span>
            </div>
          </div>
        </div>

        <div className="overview-card">
          <h3>Top sources</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                <span>Organic search</span>
                <span style={{ color: 'var(--graphite-2)' }}>42%</span>
              </div>
              <div style={{ height: '6px', backgroundColor: 'rgba(244,241,234,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: '42%', backgroundColor: 'var(--accent)' }}></div>
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                <span>Direct</span>
                <span style={{ color: 'var(--graphite-2)' }}>28%</span>
              </div>
              <div style={{ height: '6px', backgroundColor: 'rgba(244,241,234,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: '28%', backgroundColor: 'var(--accent)' }}></div>
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                <span>LinkedIn</span>
                <span style={{ color: 'var(--graphite-2)' }}>18%</span>
              </div>
              <div style={{ height: '6px', backgroundColor: 'rgba(244,241,234,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: '18%', backgroundColor: 'var(--accent)' }}></div>
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                <span>Referral</span>
                <span style={{ color: 'var(--graphite-2)' }}>9%</span>
              </div>
              <div style={{ height: '6px', backgroundColor: 'rgba(244,241,234,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: '9%', backgroundColor: 'var(--accent)' }}></div>
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                <span>Other</span>
                <span style={{ color: 'var(--graphite-2)' }}>3%</span>
              </div>
              <div style={{ height: '6px', backgroundColor: 'rgba(244,241,234,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: '3%', backgroundColor: 'var(--accent)' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="overview-grid">
        <div className="overview-card">
          <h3>Most viewed projects</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px', fontSize: '13px' }}>
            {projects.sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 5).map((project, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{project.name}</span>
                <span style={{ color: 'var(--graphite-2)' }}>{project.views || 0}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="overview-card">
          <h3>Enquiry funnel</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                <span>Contact views</span>
                <strong>412</strong>
              </div>
              <div style={{ height: '6px', backgroundColor: 'rgba(244,241,234,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: '100%', backgroundColor: 'var(--accent)' }}></div>
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                <span>Form starts</span>
                <strong>38</strong>
              </div>
              <div style={{ height: '6px', backgroundColor: 'rgba(244,241,234,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: '38px', backgroundColor: 'var(--accent)' }}></div>
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                <span>Submitted</span>
                <strong>14</strong>
              </div>
              <div style={{ height: '6px', backgroundColor: 'rgba(244,241,234,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: '14px', backgroundColor: 'var(--accent)' }}></div>
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                <span>Qualified</span>
                <strong>9</strong>
              </div>
              <div style={{ height: '6px', backgroundColor: 'rgba(244,241,234,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: '9px', backgroundColor: 'var(--accent)' }}></div>
              </div>
            </div>
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(244,241,234,0.08)', fontSize: '11px', color: 'var(--graphite-2)' }}>
              Conversion: 2.2% contact → qualified
            </div>
          </div>
        </div>

        <div className="overview-card">
          <h3>Workflow</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
            <div style={{ textAlign: 'center', paddingBottom: '16px', borderBottom: '1px solid rgba(244,241,234,0.08)' }}>
              <div style={{ fontSize: '32px', fontFamily: 'Instrument Serif', fontWeight: 600, color: 'var(--ivory)' }}>4</div>
              <div style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--graphite-2)', marginTop: '4px' }}>Drafts due review</div>
            </div>
            <div style={{ textAlign: 'center', paddingBottom: '16px', borderBottom: '1px solid rgba(244,241,234,0.08)' }}>
              <div style={{ fontSize: '32px', fontFamily: 'Instrument Serif', fontWeight: 600, color: '#E85D5D' }}>2</div>
              <div style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--graphite-2)', marginTop: '4px' }}>Enquiries waiting >24h</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontFamily: 'Instrument Serif', fontWeight: 600, color: '#E85D5D' }}>1</div>
              <div style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--graphite-2)', marginTop: '4px' }}>Asset missing alt text</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '40px' }}>
        <div style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--graphite-2)', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid rgba(244,241,234,0.08)' }}>
          Recent activity
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {[
            { time: '2 hours ago', actor: 'KV', action: 'Published', target: 'Aldgate Advisor Cockpit', tag: 'PUBLISHED' },
            { time: '5 hours ago', actor: 'TR', action: 'Replied to enquiry from', target: 'James Ng', tag: 'ENQUIRY' },
            { time: 'Yesterday', actor: 'AN', action: 'Uploaded 3 images to', target: 'Helix Risk Engine gallery', tag: 'MEDIA' },
            { time: 'May 6', actor: 'KV', action: 'Approved testimonial from', target: 'Sarah Johnson', tag: 'TESTIMONIAL' },
            { time: 'May 6', actor: 'TR', action: 'Created new draft project:', target: 'Vector/9 Data Platform', tag: 'DRAFT' }
          ].map((item, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '12px', paddingBottom: '12px', borderBottom: idx < 4 ? '1px solid rgba(244,241,234,0.04)' : 'none' }}>
              <span style={{ fontSize: '11px', color: 'var(--graphite-2)', minWidth: '80px' }}>{item.time}</span>
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: 'var(--ivory)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 600, flexShrink: 0 }}>
                {item.actor}
              </div>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: '13px', color: 'var(--ivory)' }}>
                  {item.action} <strong>{item.target}</strong>
                </span>
              </div>
              <span style={{ fontSize: '10px', padding: '2px 6px', backgroundColor: 'rgba(139,42,31,0.2)', color: '#E8A5A5', borderRadius: '2px' }}>
                {item.tag}
              </span>
            </div>
          ))}
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} />}
    </div>
  );
}
